import React, { createContext, useContext, useEffect, useState } from 'react';
import { virtualTokenABI } from '../assets/abis/virtualToken';
import { usePrivy } from '@privy-io/react-auth';
import { config } from '../shared/constants/WagmiConfig';
import { readContract } from 'wagmi/actions';
import { activeChain } from '../shared/utils/CheckNetwork';
import { getTokenNetwork } from '../shared/utils/TokenNetwork';
import axios from 'axios';
import { getConversionPrices } from '../api/coingecko/services';

const { JsonRpcProvider } = require('@ethersproject/providers');

interface CoinGeckoResponse {
  ethereum: {
    usd: number;
  };
  'virtual-protocol': {
    usd: number;
  };
}
interface VirtualBalanceContextType {
  virtualBalance: bigint;
  xVirtualBalance: bigint;
  ethBalance: bigint;
  xVirtualTotalSupply: bigint;
  nativeVirtualBalance: bigint;
  fetchVirtualBalance: () => void;
  fetchXVirtualBalance: () => void;
  fetchNativeVirtualBalance: () => void;
  isLoading: boolean;
  convertionPrice: CoinGeckoResponse;
}

const VirtualBalanceContext = createContext<
  VirtualBalanceContextType | undefined
>(undefined);

export const useVirtualBalanceContext = () => {
  const context = useContext(VirtualBalanceContext);
  if (!context) {
    throw new Error(
      'useVirtualBalanceContext must be used within a VirtualBalanceProvider'
    );
  }
  return context;
};

export const VirtualBalanceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user, authenticated } = usePrivy();
  const [virtualBalance, setVirtualBalance] = useState<bigint>(BigInt(0));
  const [xVirtualBalance, setXVirtualBalance] = useState<bigint>(BigInt(0));
  const [ethBalance, setEthBalance] = useState<bigint>(BigInt(0));
  const [convertionPrice, setConversionPrice] = useState({
    ethereum: {
      usd: 0,
    },
    'virtual-protocol': {
      usd: 0,
    },
    lastUpdated: null,
  });

  const [nativeVirtualBalance, setNativeVirtualBalance] = useState<bigint>(
    BigInt(0)
  );
  const [xVirtualTotalSupply, setXVirtualTotalSupply] = useState<bigint>(
    BigInt(0)
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchXVirtualBalance = async () => {
    setIsLoading(true);
    try {
      if (authenticated && user?.wallet?.address) {
        const response = await readContract(config, {
          chainId: activeChain.id,
          abi: virtualTokenABI,
          address: process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
          functionName: 'balanceOf',
          args: [user?.wallet?.address as `0x${string}`],
        });
        setXVirtualBalance(response as bigint);
      }
    } catch (error) {
      console.error('Error fetching virtual balance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVirtualBalance = async () => {
    setIsLoading(true);
    try {
      if (authenticated && user?.wallet?.address) {
        const response = await readContract(config, {
          chainId: activeChain.id,
          abi: virtualTokenABI,
          address: process.env.REACT_APP_TOKEN_ADDRESS as `0x${string}`,
          functionName: 'balanceOf',
          args: [user?.wallet?.address as `0x${string}`],
        });
        setVirtualBalance(response as bigint);
      }
    } catch (error) {
      console.error('Error fetching virtual balance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchXVirtualTotalSupply = async () => {
    setIsLoading(true);

    readContract(config, {
      chainId: activeChain.id,
      abi: virtualTokenABI,
      address: process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
      functionName: 'totalSupply',
      args: [],
    })
      .then((response) => {
        let balanceWei = response as bigint;
        setXVirtualTotalSupply(balanceWei);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchNativeVirtualBalance = async () => {
    if (authenticated && user?.wallet?.address) {
      setIsLoading(true);

      await readContract(config, {
        chainId: getTokenNetwork().id,
        abi: virtualTokenABI,
        address: process.env.REACT_APP_LOCAL_TOKEN as `0x${string}`,
        functionName: 'balanceOf',
        args: [user?.wallet?.address as `0x${string}`],
      })
        .then((response) => {
          let balanceWei = response as bigint;
          setNativeVirtualBalance(balanceWei);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const fetchEthBalance = async () => {
    if (authenticated && user?.wallet?.address) {
      const chain = activeChain;
      setIsLoading(true);
      const provider = new JsonRpcProvider(chain.rpcUrls.default.http[0]);

      const balanceWei = await provider.getBalance(user?.wallet?.address);
      setEthBalance(balanceWei);
    }
  };

  const fetchConversionPrices = async () => {
    const data = await getConversionPrices();
    setConversionPrice(data);

    localStorage.setItem(
      'conversionPricesLastUpdated',
      JSON.stringify({
        ...data,
        lastUpdated: new Date().getTime(),
      })
    );
  };

  useEffect(() => {
    const conversionPricesLastUpdated = localStorage.getItem(
      'conversionPricesLastUpdated'
    );

    const currentTime = new Date().getTime();
    if (!conversionPricesLastUpdated) {
      fetchConversionPrices();
    } else {
      const { lastUpdated, ...data } = JSON.parse(conversionPricesLastUpdated);

      const timeDiff = currentTime - lastUpdated;
      if (timeDiff >= 86400) {
        fetchConversionPrices();
      } else {
        setConversionPrice(data);
      }
    }
  }, []);

  useEffect(() => {
    if (authenticated && user?.wallet?.address) {
      fetchXVirtualBalance();
      fetchVirtualBalance();
      fetchNativeVirtualBalance();
      fetchEthBalance();
    } else {
      setXVirtualBalance(BigInt(0));
      setVirtualBalance(BigInt(0));
      setNativeVirtualBalance(BigInt(0));
      setEthBalance(BigInt(0));
    }
    fetchXVirtualTotalSupply();
  }, [user?.wallet?.address, authenticated]);

  return (
    <VirtualBalanceContext.Provider
      value={{
        virtualBalance,
        xVirtualBalance,
        ethBalance,
        nativeVirtualBalance,
        xVirtualTotalSupply,
        isLoading,
        fetchVirtualBalance,
        fetchXVirtualBalance,
        fetchNativeVirtualBalance,
        convertionPrice,
      }}
    >
      {children}
    </VirtualBalanceContext.Provider>
  );
};
